import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const RequestHoliday = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ absence_type: 'holiday', start_date: '', end_date: '', notes: '', half_day: false, half_day_period: 'am' });
  const [balance, setBalance] = useState(null);
  const [workingDays, setWorkingDays] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    api.get(`/api/holidays/my/balance?year=${new Date().getFullYear()}`).then(r => setBalance(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.half_day) {
      // A half day is always a single working day counted as 0.5
      if (form.start_date) {
        api.get(`/api/holidays/working-days?start=${form.start_date}&end=${form.start_date}`)
          .then(r => setWorkingDays(r.data.days >= 1 ? 0.5 : 0))
          .catch(() => setWorkingDays(0));
      } else {
        setWorkingDays(0);
      }
    } else if (form.start_date && form.end_date && form.end_date >= form.start_date) {
      // Server counts Mon-Fri minus England & Wales bank holidays
      api.get(`/api/holidays/working-days?start=${form.start_date}&end=${form.end_date}`)
        .then(r => setWorkingDays(r.data.days))
        .catch(() => setWorkingDays(0));
    } else {
      setWorkingDays(0);
    }
  }, [form.start_date, form.end_date, form.half_day]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.start_date) return setError('Please select a date');
    if (!form.half_day && !form.end_date) return setError('Please select start and end dates');
    if (!form.half_day && form.end_date < form.start_date) return setError('End date must be on or after start date');

    setLoading(true);
    setError('');
    try {
      const payload = form.half_day
        ? { ...form, end_date: form.start_date }
        : form;
      await api.post('/api/holidays', payload);
      setSuccess('Your request has been submitted and directors have been notified.');
      setTimeout(() => navigate('/my-holidays'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const typeOptions = [
    { value: 'holiday', label: 'Holiday', desc: 'Annual leave deducted from entitlement' },
    { value: 'sick', label: 'Sick Day', desc: 'Report absence due to illness' },
    { value: 'awol', label: 'Absent Without Leave', desc: 'Unauthorised absence' },
  ];

  const today = new Date().toISOString().split('T')[0];

  return (
    <div style={{ maxWidth: '600px' }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Request Absence</h1>
          <p className="page-subtitle">Submit a new absence request for director approval</p>
        </div>
      </div>

      {balance && user?.holiday_entitlement > 0 && (
        <div className="alert alert-info" style={{ marginBottom: '24px' }}>
          You have <strong>{balance.remaining} days</strong> of holiday remaining this year
          {balance.pending > 0 ? ` (${balance.pending} days pending approval)` : ''}.
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {/* Type selection */}
            <div className="form-group">
              <label className="form-label">Absence Type</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {typeOptions.map(opt => (
                  <label key={opt.value} style={{
                    display: 'flex', alignItems: 'center', gap: '14px',
                    padding: '14px 16px', border: '1px solid',
                    borderColor: form.absence_type === opt.value ? 'var(--red)' : 'var(--border)',
                    borderRadius: '4px', cursor: 'pointer',
                    background: form.absence_type === opt.value ? 'rgba(237,28,36,0.05)' : 'transparent',
                    transition: 'all 0.15s'
                  }}>
                    <input
                      type="radio"
                      name="absence_type"
                      value={opt.value}
                      checked={form.absence_type === opt.value}
                      onChange={handleChange}
                      style={{ accentColor: 'var(--red)' }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--navy)' }}>{opt.label}</div>
                      <div style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Half day toggle */}
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input type="checkbox" name="half_day" checked={form.half_day} onChange={handleChange} style={{ accentColor: 'var(--red)', width: '16px', height: '16px' }} />
                <span style={{ fontSize: '14px', color: 'var(--navy)', fontWeight: 500 }}>Book a half day (½ day)</span>
              </label>
            </div>

            {form.half_day ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Date</label>
                  <input type="date" name="start_date" className="form-control" value={form.start_date} onChange={handleChange} min={today} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Morning or afternoon?</label>
                  <select name="half_day_period" className="form-control" value={form.half_day_period} onChange={handleChange}>
                    <option value="am">Morning (AM)</option>
                    <option value="pm">Afternoon (PM)</option>
                  </select>
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input type="date" name="start_date" className="form-control" value={form.start_date} onChange={handleChange} min={today} required />
                </div>
                <div className="form-group">
                  <label className="form-label">End Date</label>
                  <input type="date" name="end_date" className="form-control" value={form.end_date} onChange={handleChange} min={form.start_date || today} required />
                </div>
              </div>
            )}

            {workingDays > 0 && (
              <div style={{ background: 'var(--grey-light)', border: '1px solid var(--border)', borderRadius: '4px', padding: '12px 16px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: 'var(--grey-mid)' }}>Working days (weekends &amp; bank holidays excluded)</span>
                <strong style={{ color: 'var(--navy)', fontSize: '16px' }}>{workingDays} day{workingDays !== 1 ? 's' : ''}</strong>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Notes (optional)</label>
              <textarea name="notes" className="form-control" value={form.notes} onChange={handleChange} placeholder="Any additional information..." />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '8px' }}>
              <button type="button" className="btn btn-outline" onClick={() => navigate('/my-holidays')}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RequestHoliday;
